import { useState, useEffect } from "react";
import { Layout } from "@/components/Layout";
import { 
  Plus, Trash2, Edit2, Calendar as CalendarIcon, MapPin, FileText, 
  Users, FolderPlus, ChevronDown, ChevronRight, Radio, Headphones,
  Mic, X, Check, Palette, GripVertical, Building2, Download
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { toast } from "sonner";
import { eventsAPI, systemsAPI, venuesAPI } from "@/lib/api";

// Predefined colors for groups
const GROUP_COLORS = [
  "#34C759", // Green
  "#007AFF", // Blue
  "#FF9500", // Orange
  "#FF3B30", // Red
  "#AF52DE", // Purple
  "#5856D6", // Indigo
  "#FF2D55", // Pink
  "#00C7BE", // Teal
  "#FFD60A", // Yellow
];

export default function EventsPage({ user, onLogout }) {
  const [events, setEvents] = useState([]);
  const [systems, setSystems] = useState([]);
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showGroupDialog, setShowGroupDialog] = useState(false);
  const [showEventDetail, setShowEventDetail] = useState(false);
  const [showImportVenueDialog, setShowImportVenueDialog] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [date, setDate] = useState(null);
  const [form, setForm] = useState({
    name: "",
    location: "",
    venue_id: "",
    notes: "",
  });
  const [groupForm, setGroupForm] = useState({
    name: "",
    color: "#34C759",
    description: "",
  });

  const fetchData = async () => {
    try {
      const [eventsRes, systemsRes, venuesRes] = await Promise.all([
        eventsAPI.getAll(),
        systemsAPI.getAll(),
        venuesAPI.getAll(),
      ]);
      setEvents(eventsRes.data);
      setSystems(systemsRes.data);
      setVenues(venuesRes.data.venues || []);
    } catch (error) {
      // Error handled silently
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!date) {
      toast.error("Selecciona una fecha");
      return;
    }

    try {
      const data = {
        name: form.name,
        location: form.location,
        venue_id: form.venue_id || null,
        date: format(date, "yyyy-MM-dd"),
        notes: form.notes || null,
      };

      if (selectedEvent) {
        await eventsAPI.update(selectedEvent.id, data);
        toast.success("Evento actualizado");
      } else {
        await eventsAPI.create(data);
        toast.success("Evento creado");
      }
      setShowDialog(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al guardar evento");
    }
  };

  const handleDelete = async () => {
    try {
      await eventsAPI.delete(selectedEvent.id);
      toast.success("Evento eliminado");
      setShowDeleteDialog(false);
      setSelectedEvent(null);
      setShowEventDetail(false);
      fetchData();
    } catch (error) {
      toast.error("Error al eliminar evento");
    }
  };

  const handleImportVenueConfig = async (venueId) => {
    if (!selectedEvent || !venueId) return;
    
    try {
      const res = await eventsAPI.importVenueConfig(selectedEvent.id, venueId);
      toast.success(res.data.message);
      setShowImportVenueDialog(false);
      
      // Refresh event data
      const eventRes = await eventsAPI.getOne(selectedEvent.id);
      setSelectedEvent(eventRes.data);
      fetchData();
    } catch (error) {
      toast.error("Error al importar configuración");
    }
  };

  const handleSaveConfig = async (event) => {
    try {
      const rfConfig = systems.map((s) => ({
        system_id: s.id,
        name: s.name,
        frequency: s.frequency,
        channel: s.channel,
        status: s.status,
      }));
      await eventsAPI.update(event.id, { rf_config: rfConfig });
      toast.success("Configuración RF guardada en el evento");
      fetchData();
    } catch (error) {
      toast.error("Error al guardar configuración");
    }
  };

  const resetForm = () => {
    setForm({ name: "", location: "", venue_id: "", notes: "" });
    setDate(null);
    setSelectedEvent(null);
  };

  const openEditDialog = (event) => {
    setSelectedEvent(event);
    setForm({
      name: event.name,
      location: event.location,
      venue_id: event.venue_id || "",
      notes: event.notes || "",
    });
    setDate(new Date(event.date));
    setShowDialog(true);
  };

  const getVenueById = (id) => venues.find(v => v.id === id);

  const openEventDetail = (event) => {
    setSelectedEvent(event);
    setShowEventDetail(true);
    // Initialize all groups as expanded
    const expanded = {};
    (event.groups || []).forEach(g => expanded[g.id] = true);
    setExpandedGroups(expanded);
  };

  // ============ GROUP MANAGEMENT ============

  const handleCreateGroup = async (e) => {
    e.preventDefault();
    if (!selectedEvent) return;

    try {
      await eventsAPI.createGroup(selectedEvent.id, groupForm);
      toast.success("Grupo creado");
      setShowGroupDialog(false);
      setGroupForm({ name: "", color: "#34C759", description: "" });
      
      // Refresh event data
      const res = await eventsAPI.getOne(selectedEvent.id);
      setSelectedEvent(res.data);
      fetchData();
    } catch (error) {
      toast.error("Error al crear grupo");
    }
  };

  const handleUpdateGroup = async (e) => {
    e.preventDefault();
    if (!selectedEvent || !selectedGroup) return;

    try {
      await eventsAPI.updateGroup(selectedEvent.id, selectedGroup.id, groupForm);
      toast.success("Grupo actualizado");
      setShowGroupDialog(false);
      setSelectedGroup(null);
      setGroupForm({ name: "", color: "#34C759", description: "" });
      
      // Refresh event data
      const res = await eventsAPI.getOne(selectedEvent.id);
      setSelectedEvent(res.data);
      fetchData();
    } catch (error) {
      toast.error("Error al actualizar grupo");
    }
  };

  const handleDeleteGroup = async (groupId) => {
    if (!selectedEvent) return;

    try {
      await eventsAPI.deleteGroup(selectedEvent.id, groupId);
      toast.success("Grupo eliminado");
      
      // Refresh event data
      const res = await eventsAPI.getOne(selectedEvent.id);
      setSelectedEvent(res.data);
      fetchData();
    } catch (error) {
      toast.error("Error al eliminar grupo");
    }
  };

  const handleAddSystemToGroup = async (groupId, systemId) => {
    if (!selectedEvent) return;

    try {
      await eventsAPI.addSystemToGroup(selectedEvent.id, groupId, systemId);
      toast.success("Sistema agregado al grupo");
      
      // Refresh event data
      const res = await eventsAPI.getOne(selectedEvent.id);
      setSelectedEvent(res.data);
      fetchData();
    } catch (error) {
      toast.error("Error al agregar sistema");
    }
  };

  const handleRemoveSystemFromGroup = async (groupId, systemId) => {
    if (!selectedEvent) return;

    try {
      await eventsAPI.removeSystemFromGroup(selectedEvent.id, groupId, systemId);
      toast.success("Sistema removido del grupo");
      
      // Refresh event data
      const res = await eventsAPI.getOne(selectedEvent.id);
      setSelectedEvent(res.data);
      fetchData();
    } catch (error) {
      toast.error("Error al remover sistema");
    }
  };

  const openEditGroupDialog = (group) => {
    setSelectedGroup(group);
    setGroupForm({
      name: group.name,
      color: group.color,
      description: group.description || "",
    });
    setShowGroupDialog(true);
  };

  const toggleGroupExpand = (groupId) => {
    setExpandedGroups(prev => ({ ...prev, [groupId]: !prev[groupId] }));
  };

  // Get systems that are not in any group
  const getUnassignedSystems = () => {
    if (!selectedEvent) return systems;
    const assignedIds = new Set();
    (selectedEvent.groups || []).forEach(g => {
      (g.system_ids || []).forEach(id => assignedIds.add(id));
    });
    return systems.filter(s => !assignedIds.has(s.id));
  };

  // Get system by ID
  const getSystemById = (id) => systems.find(s => s.id === id);

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="top-bar">
        <div>
          <h1 className="font-heading text-xl font-bold text-[#F4F4F5]">
            Eventos / Shows
          </h1>
          <p className="text-sm text-[#71717A]">
            Gestión de eventos y configuraciones RF con grupos
          </p>
        </div>
        <Button
          onClick={() => {
            resetForm();
            setShowDialog(true);
          }}
          className="btn-tactical btn-tactical-primary"
          data-testid="add-event-button"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nuevo Evento
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <p className="text-[#71717A]">Cargando...</p>
        </div>
      ) : events.length === 0 ? (
        <div className="text-center py-16">
          <CalendarIcon className="w-16 h-16 mx-auto mb-4 text-[#71717A] opacity-30" />
          <h3 className="font-heading text-lg font-semibold text-[#F4F4F5] mb-2">
            Sin eventos registrados
          </h3>
          <p className="text-[#71717A] mb-6">
            Crea tu primer evento para guardar configuraciones RF
          </p>
          <Button
            onClick={() => setShowDialog(true)}
            className="btn-tactical btn-tactical-primary"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nuevo Evento
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="events-grid">
          {events.map((event) => (
            <div
              key={event.id}
              className="bg-[#111111] border border-white/[0.08] rounded-sm p-5 hover:border-white/20 transition-colors cursor-pointer"
              onClick={() => openEventDetail(event)}
              data-testid={`event-card-${event.id}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="font-heading font-semibold text-[#F4F4F5] mb-1">
                    {event.name}
                  </h3>
                  <div className="flex items-center gap-1 text-xs text-[#71717A]">
                    <MapPin className="w-3 h-3" />
                    {event.location}
                  </div>
                </div>
                <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => openEditDialog(event)}
                    className="h-7 w-7 p-0 text-[#A1A1AA] hover:text-[#F4F4F5]"
                    data-testid={`edit-event-${event.id}`}
                  >
                    <Edit2 className="w-3.5 h-3.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setSelectedEvent(event);
                      setShowDeleteDialog(true);
                    }}
                    className="h-7 w-7 p-0 text-[#FF3B30] hover:text-[#FF3B30]"
                    data-testid={`delete-event-${event.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>

              {/* Venue badge */}
              {event.venue_id && (
                <div className="flex items-center gap-2 mb-2 text-xs text-[#71717A]">
                  <Building2 className="w-3 h-3 text-blue-400" />
                  <span>{event.venue_name || getVenueById(event.venue_id)?.name || "Venue vinculado"}</span>
                </div>
              )}

              <div className="flex items-center gap-2 mb-4 text-sm">
                <CalendarIcon className="w-4 h-4 text-[#34C759]" />
                <span className="text-[#F4F4F5]">
                  {format(new Date(event.date), "PPP", { locale: es })}
                </span>
              </div>

              {/* Groups preview */}
              {event.groups && event.groups.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-3">
                  {event.groups.slice(0, 3).map(group => (
                    <Badge 
                      key={group.id} 
                      className="text-[10px] px-2 py-0.5"
                      style={{ backgroundColor: `${group.color}20`, color: group.color, borderColor: `${group.color}50` }}
                    >
                      <Users className="w-2.5 h-2.5 mr-1" />
                      {group.name}
                    </Badge>
                  ))}
                  {event.groups.length > 3 && (
                    <Badge className="text-[10px] px-2 py-0.5 bg-white/10 text-[#71717A]">
                      +{event.groups.length - 3}
                    </Badge>
                  )}
                </div>
              )}

              {event.notes && (
                <p className="text-xs text-[#71717A] mb-4 line-clamp-2">
                  {event.notes}
                </p>
              )}

              <div className="pt-4 border-t border-white/[0.08]">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs text-[#71717A]">
                    {event.groups?.length || 0} grupos • {event.rf_config?.length || 0} sistemas
                  </span>
                </div>
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    openEventDetail(event);
                  }}
                  variant="ghost"
                  size="sm"
                  className="w-full btn-tactical btn-tactical-secondary text-xs"
                >
                  <Users className="w-3 h-3 mr-2" />
                  Gestionar Grupos
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Event Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-[#111111] border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-[#F4F4F5]">
              {selectedEvent ? "Editar Evento" : "Nuevo Evento"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="form-group">
              <Label className="form-label">Nombre del Evento</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="input-tactical"
                placeholder="Ej: Concierto Teatro Municipal"
                required
                data-testid="event-name-input"
              />
            </div>

            <div className="form-group">
              <Label className="form-label">Ubicación</Label>
              <Input
                value={form.location}
                onChange={(e) => setForm({ ...form, location: e.target.value })}
                className="input-tactical"
                placeholder="Ej: Santiago, Chile"
                required
                data-testid="event-location-input"
              />
            </div>

            {/* Venue Selector */}
            {venues.length > 0 && (
              <div className="form-group">
                <Label className="form-label">
                  <Building2 className="w-3.5 h-3.5 inline mr-1" />
                  Vincular a Venue (opcional)
                </Label>
                <Select
                  value={form.venue_id || "none"}
                  onValueChange={(value) => {
                    const actualValue = value === "none" ? "" : value;
                    const venue = venues.find(v => v.id === actualValue);
                    setForm({ 
                      ...form, 
                      venue_id: actualValue,
                      location: venue ? `${venue.name}, ${venue.city}` : form.location
                    });
                  }}
                >
                  <SelectTrigger className="input-tactical">
                    <SelectValue placeholder="Seleccionar venue guardado..." />
                  </SelectTrigger>
                  <SelectContent className="bg-[#111111] border-white/10">
                    <SelectItem value="none">Sin venue</SelectItem>
                    {venues.map(venue => (
                      <SelectItem key={venue.id} value={venue.id}>
                        <div className="flex items-center gap-2">
                          <Building2 className="w-3 h-3 text-blue-400" />
                          {venue.name} - {venue.city}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[10px] text-[#71717A] mt-1">
                  Al vincular, se importará la configuración RF del venue
                </p>
              </div>
            )}

            <div className="form-group">
              <Label className="form-label">Fecha</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={`w-full justify-start text-left font-normal input-tactical ${
                      !date && "text-[#71717A]"
                    }`}
                    data-testid="event-date-trigger"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {date ? format(date, "PPP", { locale: es }) : "Seleccionar fecha"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-[#111111] border-white/10">
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={setDate}
                    initialFocus
                    locale={es}
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="form-group">
              <Label className="form-label">Notas (opcional)</Label>
              <Textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="input-tactical min-h-[80px]"
                placeholder="Información adicional..."
                data-testid="event-notes-input"
              />
            </div>

            <div className="flex gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                className="flex-1 border-white/10"
                onClick={() => setShowDialog(false)}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                className="flex-1 btn-tactical btn-tactical-primary"
                data-testid="save-event-button"
              >
                {selectedEvent ? "Guardar" : "Crear Evento"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Event Detail with Groups Dialog */}
      <Dialog open={showEventDetail} onOpenChange={setShowEventDetail}>
        <DialogContent className="bg-[#111111] border-white/10 max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <div>
                <DialogTitle className="font-heading text-[#F4F4F5] text-xl">
                  {selectedEvent?.name}
                </DialogTitle>
                <p className="text-sm text-[#71717A] mt-1">
                  <MapPin className="w-3 h-3 inline mr-1" />
                  {selectedEvent?.location} • {selectedEvent && format(new Date(selectedEvent.date), "PPP", { locale: es })}
                  {selectedEvent?.venue_name && (
                    <span className="ml-2">
                      <Building2 className="w-3 h-3 inline mr-1 text-blue-400" />
                      {selectedEvent.venue_name}
                    </span>
                  )}
                </p>
              </div>
              <div className="flex gap-2">
                {venues.length > 0 && (
                  <Button
                    onClick={() => setShowImportVenueDialog(true)}
                    variant="outline"
                    size="sm"
                    className="border-blue-500/30 text-blue-400 hover:bg-blue-500/10"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Importar de Venue
                  </Button>
                )}
                <Button
                  onClick={() => handleSaveConfig(selectedEvent)}
                  variant="outline"
                  size="sm"
                  className="border-white/10"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Guardar Config RF
                </Button>
                <Button
                  onClick={() => {
                    setSelectedGroup(null);
                    setGroupForm({ name: "", color: GROUP_COLORS[Math.floor(Math.random() * GROUP_COLORS.length)], description: "" });
                    setShowGroupDialog(true);
                  }}
                  className="btn-tactical btn-tactical-primary"
                  size="sm"
                >
                  <FolderPlus className="w-4 h-4 mr-2" />
                  Nuevo Grupo
                </Button>
              </div>
            </div>
          </DialogHeader>

          <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Groups Column */}
            <div className="lg:col-span-2 space-y-3">
              <h3 className="text-sm font-medium text-[#71717A] mb-3">Grupos del Evento</h3>
              
              {(!selectedEvent?.groups || selectedEvent.groups.length === 0) ? (
                <div className="text-center py-12 bg-[#0A0A0A] rounded-lg border border-white/[0.05]">
                  <Users className="w-12 h-12 mx-auto mb-3 text-[#71717A] opacity-30" />
                  <p className="text-[#71717A] text-sm mb-4">No hay grupos creados</p>
                  <Button
                    onClick={() => {
                      setSelectedGroup(null);
                      setGroupForm({ name: "", color: "#34C759", description: "" });
                      setShowGroupDialog(true);
                    }}
                    size="sm"
                    className="btn-tactical btn-tactical-primary"
                  >
                    <FolderPlus className="w-4 h-4 mr-2" />
                    Crear Primer Grupo
                  </Button>
                </div>
              ) : (
                selectedEvent.groups.map(group => (
                  <div 
                    key={group.id} 
                    className="bg-[#0A0A0A] rounded-lg border border-white/[0.05] overflow-hidden"
                    style={{ borderLeftColor: group.color, borderLeftWidth: '3px' }}
                  >
                    {/* Group Header */}
                    <div 
                      className="flex items-center justify-between p-3 cursor-pointer hover:bg-white/[0.02]"
                      onClick={() => toggleGroupExpand(group.id)}
                    >
                      <div className="flex items-center gap-3">
                        {expandedGroups[group.id] ? (
                          <ChevronDown className="w-4 h-4 text-[#71717A]" />
                        ) : (
                          <ChevronRight className="w-4 h-4 text-[#71717A]" />
                        )}
                        <div 
                          className="w-3 h-3 rounded-full" 
                          style={{ backgroundColor: group.color }}
                        />
                        <span className="font-medium text-[#F4F4F5]">{group.name}</span>
                        <Badge className="text-[10px] bg-white/10 text-[#71717A]">
                          {group.system_ids?.length || 0} sistemas
                        </Badge>
                      </div>
                      <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditGroupDialog(group)}
                          className="h-7 w-7 p-0 text-[#A1A1AA] hover:text-[#F4F4F5]"
                        >
                          <Edit2 className="w-3 h-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteGroup(group.id)}
                          className="h-7 w-7 p-0 text-[#FF3B30] hover:text-[#FF3B30]"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>

                    {/* Group Systems */}
                    {expandedGroups[group.id] && (
                      <div className="border-t border-white/[0.05] p-3 space-y-2">
                        {group.description && (
                          <p className="text-xs text-[#71717A] mb-3">{group.description}</p>
                        )}
                        
                        {(!group.system_ids || group.system_ids.length === 0) ? (
                          <p className="text-xs text-[#71717A] py-4 text-center">
                            Arrastra sistemas aquí o selecciónalos de la lista
                          </p>
                        ) : (
                          <div className="space-y-1">
                            {group.system_ids.map(sysId => {
                              const system = getSystemById(sysId);
                              if (!system) return null;
                              return (
                                <div 
                                  key={sysId}
                                  className="flex items-center justify-between p-2 bg-[#111111] rounded border border-white/[0.05]"
                                >
                                  <div className="flex items-center gap-2">
                                    {system.system_type === "microphone" ? (
                                      <Mic className="w-3.5 h-3.5 text-blue-400" />
                                    ) : (
                                      <Headphones className="w-3.5 h-3.5 text-[#34C759]" />
                                    )}
                                    <span className="text-sm text-[#F4F4F5]">{system.name}</span>
                                    <span className="text-xs text-[#71717A]">
                                      {system.frequency?.toFixed(3)} MHz
                                    </span>
                                  </div>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleRemoveSystemFromGroup(group.id, sysId)}
                                    className="h-6 w-6 p-0 text-[#FF3B30] hover:text-[#FF3B30]"
                                  >
                                    <X className="w-3 h-3" />
                                  </Button>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>

            {/* Available Systems Column */}
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-[#71717A] mb-3">
                Sistemas Disponibles
                <span className="ml-2 text-[#34C759]">({getUnassignedSystems().length})</span>
              </h3>
              
              <div className="bg-[#0A0A0A] rounded-lg border border-white/[0.05] max-h-[500px] overflow-y-auto">
                {getUnassignedSystems().length === 0 ? (
                  <div className="p-6 text-center">
                    <Check className="w-8 h-8 mx-auto mb-2 text-[#34C759]" />
                    <p className="text-xs text-[#71717A]">Todos los sistemas están asignados</p>
                  </div>
                ) : (
                  <div className="p-2 space-y-1">
                    {getUnassignedSystems().map(system => (
                      <div 
                        key={system.id}
                        className="p-2 bg-[#111111] rounded border border-white/[0.05] hover:border-white/20 transition-colors"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            {system.system_type === "microphone" ? (
                              <Mic className="w-3.5 h-3.5 text-blue-400" />
                            ) : (
                              <Headphones className="w-3.5 h-3.5 text-[#34C759]" />
                            )}
                            <div>
                              <p className="text-sm text-[#F4F4F5]">{system.name}</p>
                              <p className="text-[10px] text-[#71717A]">
                                {system.model} • {system.frequency?.toFixed(3)} MHz
                              </p>
                            </div>
                          </div>
                        </div>
                        
                        {/* Quick assign to group */}
                        {selectedEvent?.groups && selectedEvent.groups.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {selectedEvent.groups.map(group => (
                              <Button
                                key={group.id}
                                variant="ghost"
                                size="sm"
                                onClick={() => handleAddSystemToGroup(group.id, system.id)}
                                className="h-6 px-2 text-[10px]"
                                style={{ color: group.color }}
                              >
                                <Plus className="w-3 h-3 mr-1" />
                                {group.name}
                              </Button>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create/Edit Group Dialog */}
      <Dialog open={showGroupDialog} onOpenChange={setShowGroupDialog}>
        <DialogContent className="bg-[#111111] border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-[#F4F4F5]">
              {selectedGroup ? "Editar Grupo" : "Nuevo Grupo"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={selectedGroup ? handleUpdateGroup : handleCreateGroup} className="space-y-4">
            <div className="form-group">
              <Label className="form-label">Nombre del Grupo</Label>
              <Input
                value={groupForm.name}
                onChange={(e) => setGroupForm({ ...groupForm, name: e.target.value })}
                className="input-tactical"
                placeholder="Ej: Banda Principal, Coros, Staff"
                required
              />
            </div>

            <div className="form-group">
              <Label className="form-label">Color</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {GROUP_COLORS.map(color => (
                  <button
                    key={color}
                    type="button"
                    onClick={() => setGroupForm({ ...groupForm, color })}
                    className={`w-8 h-8 rounded-full transition-all ${
                      groupForm.color === color ? "ring-2 ring-white ring-offset-2 ring-offset-[#111111]" : ""
                    }`}
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>
            </div>

            <div className="form-group">
              <Label className="form-label">Descripción (opcional)</Label>
              <Textarea
                value={groupForm.description}
                onChange={(e) => setGroupForm({ ...groupForm, description: e.target.value })}
                className="input-tactical min-h-[60px]"
                placeholder="Descripción del grupo..."
              />
            </div>

            <div className="flex gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                className="flex-1 border-white/10"
                onClick={() => {
                  setShowGroupDialog(false);
                  setSelectedGroup(null);
                }}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                className="flex-1 btn-tactical btn-tactical-primary"
              >
                {selectedGroup ? "Guardar" : "Crear Grupo"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent className="bg-[#111111] border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#F4F4F5]">
              ¿Eliminar evento?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#71717A]">
              Esta acción no se puede deshacer. Se eliminará el evento
              "{selectedEvent?.name}" y todos sus grupos.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-white/10">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-[#FF3B30] hover:bg-[#FF3B30]/90"
              data-testid="confirm-delete-event"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Import from Venue Dialog */}
      <Dialog open={showImportVenueDialog} onOpenChange={setShowImportVenueDialog}>
        <DialogContent className="bg-[#111111] border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-[#F4F4F5] flex items-center gap-2">
              <Download className="w-5 h-5 text-blue-400" />
              Importar Configuración de Venue
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <p className="text-sm text-[#71717A]">
              Selecciona un venue para importar su configuración RF guardada al evento actual.
            </p>
            
            {venues.length === 0 ? (
              <div className="text-center py-8 bg-[#0A0A0A] rounded-lg border border-white/[0.05]">
                <Building2 className="w-10 h-10 mx-auto mb-3 text-[#71717A] opacity-30" />
                <p className="text-sm text-[#71717A]">No tienes venues guardados</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {venues.map(venue => (
                  <button
                    key={venue.id}
                    onClick={() => handleImportVenueConfig(venue.id)}
                    className="w-full p-4 bg-[#0A0A0A] rounded-lg border border-white/[0.05] hover:border-blue-500/30 transition-colors text-left"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium text-[#F4F4F5]">{venue.name}</h4>
                        <p className="text-xs text-[#71717A]">{venue.city}</p>
                      </div>
                      <div className="text-right">
                        <Badge className="bg-blue-500/20 text-blue-400 text-[10px]">
                          {venue.frequencies?.length || 0} frecuencias
                        </Badge>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
          
          <div className="flex justify-end pt-4">
            <Button
              variant="outline"
              className="border-white/10"
              onClick={() => setShowImportVenueDialog(false)}
            >
              Cancelar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
